import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import {
  Ban,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Sparkles,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { assetUrl } from "@/lib/api";
import { orgScopedPath } from "@/lib/orgDoor";
import { useModuleNewTag, useModuleVisibility } from "@/lib/moduleVisibility";
import { useSidebarDrawer } from "@/components/SidebarDrawerContext";
import { cn, titleCase } from "@/lib/utils";

export type NavItem = {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Static text badge (e.g. "New", "AI", or a preformatted count). */
  badge?: string;
  badgeTone?: "ai" | "new" | "count";
  /** Live numeric badge — only rendered when greater than zero. */
  badgeCount?: number;
  /** Optional click handler (e.g. to clear a count). */
  onClick?: () => void;
  /** When set, render a trailing delete button (e.g. for saved filters). */
  onDelete?: () => void;
  /** When set, render a trailing rename button that edits the label inline. */
  onRename?: (name: string) => void;
  /** Child links — the item renders as an expandable group instead of a link
      (e.g. Settings → My Profile / Password & Security / …). */
  children?: { label: string; to: string; icon: LucideIcon }[];
  /** Registry key from app/services/module_visibility.py. When set, the item is
      only rendered if the super admin has this module switched on for a role the
      viewer holds. Items without one (Home, Settings, Support) are core
      navigation and can never be switched off. */
  module?: string;
};

export type NavSection = {
  heading: string;
  items: NavItem[];
};

function NavBadge({ tone, children }: { tone?: NavItem["badgeTone"]; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        // Compact on purpose: the label truncates before the pill does, so a
        // wide pill ate module names ("Upcoming Modul…", "Funding Marke…").
        "ml-auto shrink-0 rounded-full border px-1.5 py-[0.2rem] text-[0.5rem] font-bold uppercase leading-none tracking-[0.04em]",
        tone === "ai"
          ? "border-violet-600 bg-violet-600 text-white"
          : tone === "count"
            ? "border-primary/40 bg-primary/15 text-primary tabular-nums"
            : "border-emerald-600 bg-emerald-600 text-white"
      )}
    >
      {children}
    </span>
  );
}

function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">
      {count > 99 ? "99+" : count}
    </span>
  );
}

/**
 * Shared, collapsible app sidebar. Driven entirely by `sections` so the same
 * component can back the buyer, admin, and BL advisor layouts — only the nav
 * config differs. State (collapsed/expanded) is persisted per `storageKey`.
 */
export default function AppSidebar({
  sections,
  homeUrl,
  storageKey,
  logoutPath = "/sign-in",
  upgradeCta,
}: {
  sections: NavSection[];
  homeUrl: string;
  storageKey: string;
  logoutPath?: string;
  /** Optional upgrade call-to-action pinned above the sign-out row (e.g. for
      free-tier buyers). Hidden when unset. */
  upgradeCta?: { to: string; label: string; blurb?: string } | null;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // Null on pages rendered outside AppShell — those have no drawer to open.
  const drawer = useSidebarDrawer();

  // Every workspace URL carries the user's organization — /ecfa/buyer/...,
  // /ecfa/admin/... — so the address bar always names the organization. Done
  // here, once, rather than in each sidebar's link table.
  const orgPath = (path: string) => orgScopedPath(path, user?.org_slug);

  const handleLogout = () => {
    logout();
    navigate(orgPath(logoutPath));
  };

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(storageKey) === "1";
  });

  // Expanded/collapsed state per child-group label. Unset = auto: a group with
  // an active child starts open.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  // The item whose label is being edited inline (saved-filter rename).
  const [renaming, setRenaming] = useState<{ key: string; value: string } | null>(null);
  const renameDoneRef = useRef(false);

  useEffect(() => {
    window.localStorage.setItem(storageKey, collapsed ? "1" : "0");
  }, [collapsed, storageKey]);

  /* Super-admin module switchboard, plus the in-code release gate the hook
     folds in (RELEASE_GATED in lib/moduleVisibility.ts — where a not-yet-
     launched module is held back, for every surface at once rather than the nav
     alone).
     Filtering here rather than in each sidebar means all three (and any future
     one) get it from the single place they already share, and a section left
     with no visible items disappears with its heading instead of leaving a bare
     label behind. */
  const isModuleVisible = useModuleVisibility();
  /* The super admin's "New" tag rides along here for the same reason: one place
     for every sidebar. It only fills in a badge the item does not already have,
     so an explicit "AI" / "Soon" / live-count pill is never overwritten — an
     item can only wear one badge, and the one written at the call site is the
     more specific of the two. */
  const isModuleNew = useModuleNewTag();
  const visibleSections = sections
    .map((section) => ({
      ...section,
      items: section.items
        .filter((i) => isModuleVisible(i.module))
        .map((i) =>
          !i.badge && isModuleNew(i.module) ? { ...i, badge: "New", badgeTone: "new" as const } : i,
        ),
    }))
    .filter((section) => section.items.length > 0);

  // The active item is the single best (longest) matching path: an exact match,
  // or a path prefix for child routes. Picking the longest match stops a parent
  // item (e.g. /admin/listings) from also highlighting when a more specific
  // sibling (/admin/listings/deleted) is the real match.
  const bestMatch = visibleSections
    .flatMap((s) => s.items.flatMap((i) => [i.to, ...(i.children?.map((c) => c.to) ?? [])]))
    .map(orgPath)
    .filter((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0];
  const isActive = (path: string) => path === bestMatch;

  /* The nav body itself — shared verbatim by the desktop rail and the mobile
     drawer, so the two can never drift apart. `isCollapsed` is a parameter
     rather than the state above because the drawer is always expanded. */
  const panel = (isCollapsed: boolean, inDrawer = false) => (
      <div className="flex h-full flex-col [font-family:var(--font-display)]">
        {/* Header row aligns with the top navigation bar height; logo centered. */}
        <div className={cn("flex h-16 items-center px-3", inDrawer ? "justify-start pr-12" : "justify-center")}>
          <Link to={orgPath(homeUrl)} className="flex min-w-0 items-center justify-center">
            {isCollapsed ? (
              <img src="/logo_mark.png" alt="BusinessLocating" className="h-10 w-10 object-contain" />
            ) : (
              // Same size as the footer's logo (h-12) — h-14 crowded the header row
              <BrandLogo className="h-12 w-auto" />
            )}
          </Link>
        </div>

        <nav className="flex-1 space-y-5 overflow-y-auto px-2 py-4">
          {visibleSections.map((section) => (
            <div key={section.heading}>
              {!isCollapsed && (
                <div className="px-3 pb-2 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  {section.heading}
                </div>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const active = isActive(item.to);
                  const Icon = item.icon;

                  // Expandable group: parent toggles, children are the links.
                  if (item.children?.length) {
                    const childActive = item.children.some((c) => isActive(c.to));
                    const expanded = openGroups[item.label] ?? childActive;

                    if (isCollapsed) {
                      // No room for children — the icon deep-links to the first child.
                      return (
                        <Link
                          key={item.to + item.label}
                          to={orgPath(item.children[0].to)}
                          title={item.label}
                          className={cn(
                            "group flex items-center justify-center rounded-lg py-2 px-0 text-sm font-medium transition-colors duration-200",
                            childActive ? "text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                          )}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                        </Link>
                      );
                    }

                    return (
                      <div key={item.to + item.label}>
                        <button
                          type="button"
                          onClick={() => setOpenGroups((g) => ({ ...g, [item.label]: !(g[item.label] ?? childActive) }))}
                          className={cn(
                            "group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200",
                            childActive ? "font-semibold text-foreground" : "text-foreground hover:bg-accent"
                          )}
                        >
                          <Icon className={cn("h-4 w-4 shrink-0", childActive ? "text-primary" : "text-muted-foreground")} />
                          <span className="truncate">{item.label}</span>
                          {/* Groups wear their badge too. This row used to go
                              straight from label to chevron, so a module that
                              was both expandable and flagged New — Resource is
                              the only one — had its pill computed above and
                              then silently dropped here, while every flat item
                              next to it showed one. NavBadge brings its own
                              ml-auto, so it takes the slack and the chevron
                              stays pinned to the right of it. */}
                          {item.badge && <NavBadge tone={item.badgeTone}>{item.badge}</NavBadge>}
                          <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform duration-200", !item.badge && "ml-auto", expanded && "rotate-180")} />
                        </button>
                        {expanded && (
                          <div className="ml-[1.15rem] mt-0.5 space-y-0.5 border-l border-border/60 pl-2">
                            {item.children.map((child) => {
                              const ChildIcon = child.icon;
                              const childIsActive = isActive(child.to);
                              return (
                                <Link
                                  key={child.to}
                                  to={orgPath(child.to)}
                                  className={cn(
                                    "flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[0.82rem] font-medium transition-colors duration-200",
                                    childIsActive
                                      ? "bg-gradient-to-r from-primary/25 to-primary/5 font-semibold text-primary shadow-[inset_2px_0_0_0_currentColor]"
                                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                  )}
                                >
                                  <ChildIcon className={cn("h-3.5 w-3.5 shrink-0", childIsActive ? "text-primary" : "text-muted-foreground")} />
                                  <span className="truncate">{child.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  const itemKey = item.to + item.label;
                  if (!isCollapsed && item.onRename && renaming?.key === itemKey) {
                    const commit = () => {
                      // Enter unmounts the input, which can also fire blur.
                      if (renameDoneRef.current) return;
                      renameDoneRef.current = true;
                      const next = renaming.value.trim();
                      setRenaming(null);
                      if (next && next !== item.label) item.onRename?.(next);
                    };
                    return (
                      <div key={itemKey} className="flex items-center gap-2.5 rounded-lg bg-accent px-3 py-1.5">
                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <input
                          autoFocus
                          aria-label={`Rename ${item.label}`}
                          value={renaming.value}
                          maxLength={120}
                          onChange={(e) => setRenaming({ key: itemKey, value: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commit();
                            else if (e.key === "Escape") { renameDoneRef.current = true; setRenaming(null); }
                          }}
                          onBlur={commit}
                          className="min-w-0 flex-1 rounded border border-border/70 bg-background px-1.5 py-0.5 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
                        />
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={itemKey}
                      to={orgPath(item.to)}
                      // Saved-filter names truncate; the tooltip shows the whole name.
                      title={isCollapsed || item.onRename ? item.label : undefined}
                      onClick={item.onClick}
                      className={cn(
                        "group flex items-center gap-2.5 rounded-lg py-2 text-sm font-medium transition-colors duration-200",
                        isCollapsed ? "justify-center px-0" : "px-3",
                        active
                          ? "bg-gradient-to-r from-primary/25 to-primary/5 font-semibold text-primary shadow-[inset_2px_0_0_0_currentColor]"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                      )}
                    >
                      <Icon className={cn("h-4 w-4 shrink-0", active ? "text-primary" : "text-muted-foreground")} />
                      {!isCollapsed && <span className="truncate">{item.label}</span>}
                      {!isCollapsed && item.badge && <NavBadge tone={item.badgeTone}>{item.badge}</NavBadge>}
                      {!isCollapsed && item.badgeCount != null && <CountBadge count={item.badgeCount} />}
                      {!isCollapsed && item.onRename && (
                        <button
                          type="button"
                          aria-label={`Rename ${item.label}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            renameDoneRef.current = false;
                            setRenaming({ key: itemKey, value: item.label });
                          }}
                          className={cn(
                            "ml-auto rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100",
                            active ? "text-white/80 hover:bg-white/20" : "text-muted-foreground/60 hover:bg-accent hover:text-foreground"
                          )}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {!isCollapsed && item.onDelete && (
                        <button
                          type="button"
                          aria-label={`Delete ${item.label}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            item.onDelete?.();
                          }}
                          className={cn(
                            "rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100",
                            !item.onRename && "ml-auto",
                            active ? "text-white/80 hover:bg-white/20" : "text-muted-foreground/60 hover:bg-destructive/10 hover:text-destructive"
                          )}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Upgrade CTA — pinned above sign-out for free-tier users. */}
        {upgradeCta && (
          <div className={cn("shrink-0", isCollapsed ? "px-2 py-2" : "px-3 py-3")}>
            {isCollapsed ? (
              <Link
                to={upgradeCta.to}
                title={upgradeCta.label}
                className="flex h-9 w-full items-center justify-center rounded-lg bg-amber-400 text-slate-900 shadow-sm transition-opacity hover:opacity-90"
              >
                <Sparkles className="h-4 w-4" />
              </Link>
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                {upgradeCta.blurb && (
                  <p className="mb-2 text-[0.7rem] leading-snug text-amber-900/80">{upgradeCta.blurb}</p>
                )}
                <Link
                  to={upgradeCta.to}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-amber-400 py-2 text-xs font-extrabold text-slate-900 shadow-sm transition-opacity hover:opacity-90"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {upgradeCta.label}
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Profile + sign out — pinned to the bottom of the sidebar. */}
        <div className={cn("shrink-0 space-y-2 py-3", isCollapsed ? "px-2" : "px-3")}>
          {user && (
            <div
              className={cn(
                "flex items-center rounded-xl bg-accent/70",
                isCollapsed ? "justify-center p-1.5" : "gap-2.5 p-2.5"
              )}
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/15 text-xs font-bold text-primary">
                {user.avatar_url ? (
                  <img src={assetUrl(user.avatar_url)!} alt="" className="h-full w-full object-cover" />
                ) : (
                  <>
                    {user.first_name?.[0]?.toUpperCase()}
                    {user.last_name?.[0]?.toUpperCase()}
                  </>
                )}
              </div>
              {!isCollapsed && (
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold leading-tight text-foreground">
                    {titleCase(`${user.first_name ?? ""} ${user.last_name ?? ""}`)}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={handleLogout}
            title={isCollapsed ? "Sign Out" : undefined}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-xl border border-border/80 bg-card py-2 text-sm font-semibold text-rose-600 transition-colors duration-200 hover:border-rose-500/40 hover:bg-rose-500/10",
              isCollapsed ? "px-0" : "px-3"
            )}
          >
            <Ban className="h-4 w-4 shrink-0" />
            {!isCollapsed && <span>Sign Out</span>}
          </button>
        </div>
      </div>
  );

  return (
    <>
      <aside
        className={cn(
          "sticky top-0 z-40 hidden h-screen shrink-0 bg-card/60 transition-[width] duration-200 lg:block",
          collapsed ? "w-16" : "w-64"
        )}
      >
        {/* Collapse / expand — a chip centred ON the rail's right edge, just
            below the header.

            `translate-x-1/2` against `right-0` is what does the work: the button
            straddles the boundary, half over the sidebar and half over the page,
            so it reads as a handle attached to the rail. It previously sat at
            `left-full ml-2`, i.e. wholly inside the content column with a gap
            behind it, which made it look like a control belonging to whatever
            page you happened to be on.

            `top-20` keeps it clear of the sticky top nav (h-16, z-50), and the
            background ring separates it from both surfaces it overlaps. Only the
            desktop rail has it: this <aside> is `hidden lg:block`, and the mobile
            drawer closes from its own header button. */}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute right-0 top-20 z-[60] flex h-7 w-7 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-lg bg-gold-gradient text-primary-foreground shadow-md ring-2 ring-background transition-[filter] hover:brightness-[1.06]"
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        </button>

      {panel(collapsed)}
      </aside>

      {/* Mobile: the same navigation, as an off-canvas drawer. The rail above
          is hidden below `lg`, so without this a phone reaches none of it —
          the header's hamburger owns the open state (SidebarDrawerContext). */}
      {drawer?.open && (
        <div className="fixed inset-0 z-[70] lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 bg-foreground/50 backdrop-blur-[2px]"
            onClick={drawer.close}
          />
          <div className="absolute inset-y-0 left-0 flex w-[17.5rem] max-w-[86vw] flex-col overflow-hidden border-r border-border/70 bg-card shadow-2xl">
            {/* The drawer covers the header, so the hamburger that opened it is
                no longer reachable — it needs its own close affordance. */}
            <button
              type="button"
              onClick={drawer.close}
              aria-label="Close navigation"
              className="absolute right-2 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-lg border border-border/80 bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
            {panel(false, true)}
          </div>
        </div>
      )}
    </>
  );
}
